import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { api } from '../../../../singleton/axios';
import { Watchlist } from './useWatchlists';

async function deleteWatchlist(watchlistId: string): Promise<void> {
  try {
    await api.delete(`/stock-watchlists/${watchlistId}`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.error || 
      `Failed to delete watchlist: ${error.response?.status || 'Unknown error'}`
    );
  }
}

export function useDeleteWatchlist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteWatchlist,
    onSuccess: (_, deletedWatchlistId) => {
      // Get the watchlist name before removing it from cache
      const watchlists = queryClient.getQueryData<Watchlist[]>(['watchlists']) || [];
      const deletedWatchlist = watchlists.find(w => w.id === deletedWatchlistId);
      
      // Optimistically update the cache
      queryClient.setQueryData(['watchlists'], (old: Watchlist[] = []) => {
        return old.filter(watchlist => watchlist.id !== deletedWatchlistId);
      });
      
      // Also invalidate specific watchlist query if it exists
      queryClient.removeQueries({
        queryKey: ['watchlist', deletedWatchlistId]
      });

      // Removing a watchlist removes its holdings from every surface that
      // reads them — the dashboard card, the list itself, and the shuffled
      // charts/compare/chat tiles.
      queryClient.invalidateQueries({ queryKey: ['watchlists'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-watchlist'] });
      queryClient.invalidateQueries({ queryKey: ['watchlist-earnings'] });
      queryClient.invalidateQueries({ queryKey: ['watchlist-earnings-feed'] });
      queryClient.invalidateQueries({ queryKey: ['watchlist-earnings-feed-head'] });
      queryClient.invalidateQueries({ queryKey: ['chartsWatchlist'] });
      
      // Show success notification
      const watchlistName = deletedWatchlist?.name || 'Watchlist';
      enqueueSnackbar(`"${watchlistName}" deleted successfully`, {
        variant: 'success',
        autoHideDuration: 3000,
      });
    },
    onError: (error) => {
      // Show error notification
      enqueueSnackbar(error.message || 'Failed to delete watchlist', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    },
  });
}